// src/App.jsx 
import React, { useState, useEffect } from "react";
import { MapPin, Droplets, RefreshCw, AlertCircle, Filter, Cloud } from "lucide-react"; // 🔧 新增Cloud图标
import FloodMap from "./components/FloodMap";
import EventDetails from "./components/dashboard/EventDetails";
import TrendPanel from "./components/TrendPanel";
import Button from "./components/ui/Button/Button";
import {
  loadSensorData,
  filterSensors,
  getSensorStats,
  ALLOWED_BOROUGHS,
} from "./utils/dataProcessor";

function App() {
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);


  const [filters, setFilters] = useState({
    borough: "all",
    dateRange: "week",
    severity: "all",
  });

  const [allSensors, setAllSensors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [sensorStats, setSensorStats] = useState(null);


  const [_windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });

      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    setTimeout(() => {
      handleResize();
    }, 300);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

 
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        console.log("Loading sensor data from CSV (Brooklyn & Queens only)...");

        const sensors = await loadSensorData();
        setAllSensors(sensors);
        setSensorStats(getSensorStats(sensors));
        setLastUpdated(new Date());

        console.log(
          `Loaded ${sensors.length} sensors from Brooklyn and Queens successfully`
        );
      } catch (error) {
        console.error("Failed to load sensor data:", error);
        setLoadError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);


  useEffect(() => {
    if (!isAutoRefresh || isLoading) return;

    const interval = setInterval(async () => {
      try {
        console.log("Auto-refreshing data...");
        const sensors = await loadSensorData();
        setAllSensors(sensors);
        setSensorStats(getSensorStats(sensors));
        setLastUpdated(new Date());
      } catch (error) {
        console.error("Auto-refresh failed:", error);
      }
    }, 300000); 

    return () => clearInterval(interval);
  }, [isAutoRefresh, isLoading]);


  useEffect(() => {
    console.log("🌦️ Weather alert system initialized");

  }, []);


  const handleManualRefresh = async () => {
    try {
      setIsLoading(true);
      console.log("Manual refresh triggered");
      const sensors = await loadSensorData();
      setAllSensors(sensors);
      setSensorStats(getSensorStats(sensors));
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Manual refresh failed:", error);
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSensorSelect = (sensor) => {
    console.log("Selected sensor:", sensor);
    setSelectedSensor(sensor);
    if (sensor.events && sensor.events.length > 0) {
      setSelectedEvent(sensor.events[0]);
    }
  };


  const updateFilter = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    });
  };


  const filteredSensors =
    allSensors.length > 0 ? filterSensors(allSensors, filters) : [];
  const sensorsForMap = allSensors
    .filter((s) => s.events && s.events.length > 0)
    .map((s) => {
      const sortedEvents = [...s.events].sort(
        (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
      );
      return {
        ...s,
        latestEvent: sortedEvents[0],
      };
    });


  const severityCounts = {
    minor: allSensors.filter((s) => {
      const severity = s.events[0]?.severity?.toLowerCase();
      return severity === "minor" || severity === "mild"; 
    }).length,
    moderate: allSensors.filter(
      (s) => s.events[0]?.severity?.toLowerCase() === "moderate"
    ).length,
    severe: allSensors.filter((s) => {
      const severity = s.events[0]?.severity?.toLowerCase();
      return severity === "severe" || severity === "major"; 
    }).length,
  };

  const boroughCounts = {};
  ALLOWED_BOROUGHS.forEach((borough) => {
    boroughCounts[borough] = allSensors.filter(
      (s) => s.borough === borough
    ).length;
  });


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg border max-w-md">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Loading Brooklyn & Queens Flood Data
          </h2>
          <p className="text-gray-600 mb-4">
            Reading sensor deployment data for Brooklyn and Queens...
          </p>
          <div className="text-sm text-gray-500">
            This may take a moment while we process the sensor locations.
          </div>

          <div className="text-xs text-blue-600 mt-3 flex items-center justify-center gap-1">
            <Cloud className="h-3 w-3" />
            Weather alerts will load automatically
          </div>
        </div>
      </div>
    );
  }


  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg border max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Failed to Load Data
          </h2>
          <p className="text-gray-600 mb-4">{loadError}</p>
          <Button
            variant="primary"
            size="md"
            icon={<RefreshCw className="h-4 w-4" />}
            onClick={handleManualRefresh}
            loading={isLoading}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container bg-gray-50 min-h-screen flex flex-col">

      <header className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="flex justify-between items-center h-16 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <Droplets className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  FloodMap NYC
                </h1>
                <p className="text-xs text-gray-500">
                  Brooklyn & Queens Flood Monitoring 
                  <span className="ml-2 text-blue-600">+ Weather Alerts</span>
                </p>
              </div>
            </div>


           <div className="flex items-center space-x-6 ml-auto">
              <div className="hidden sm:block text-sm text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </div>

              <Button
                variant="ghost"
                size="sm"
                icon={<RefreshCw className="h-4 w-4" />}
                onClick={handleManualRefresh}
                loading={isLoading}
                disabled={isLoading}
                title="Refresh data"
                className="p-2"
              />

              <div className="flex items-center space-x-1">
                <div
                  onClick={() => setIsAutoRefresh(!isAutoRefresh)}
                  className="cursor-pointer flex items-center space-x-1"
                  title={isAutoRefresh ? "Auto-refresh ON" : "Auto-refresh OFF"}
                >
                  <div
                    className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                      isAutoRefresh
                        ? "bg-green-500 animate-pulse"
                        : "bg-gray-400"
                    }`}
                  />
                  <span
                    className={`text-xs font-medium transition-colors duration-200 ${
                      isAutoRefresh ? "text-green-600" : "text-gray-400"
                    }`}
                  >
                    {isAutoRefresh ? "Live" : "Paused"}
                  </span>
                </div>
              </div>
            </div>
          </div>


          <div className="py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
  
              <div className="flex flex-wrap items-center gap-2 sm:gap-4">

              </div>


              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-gray-500 space-y-1 sm:space-y-0">

                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                    <span>{severityCounts.minor} minor</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <span>{severityCounts.moderate} moderate</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                    <span>{severityCounts.severe} major</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>


      <div className="flex-1 overflow-hidden bg-gray-50 py-15">

        <div className="h-full flex justify-center">
          <div className="w-full max-w-[1600px] px-6 py-6">

            <div
              className="hidden lg:grid gap-8 h-full"
              style={{
                gridTemplateColumns: "2fr 2fr 1fr",
                minWidth: "1200px",
                padding: "0 4px", 
              }}
            >

              <div className="bg-white rounded-lg shadow-sm border flex flex-col overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex-shrink-0">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                    Sensors location

                    <Cloud className="h-4 w-4 ml-2 text-gray-400" title="Weather alerts enabled" />
                  </h2>
                </div>
                <div className="flex-1 map-wrapper">
                  <FloodMap
                    sensors={sensorsForMap}
                    selectedSensor={selectedSensor}
                    onSensorSelect={handleSensorSelect}
                  />
                </div>
              </div>

 
              <div className="flex flex-col min-h-0 overflow-hidden">
                <TrendPanel selectedSensor={selectedSensor} />
              </div>

   
              <div className="flex flex-col min-h-0 overflow-hidden">
                <EventDetails
                  selectedSensor={selectedSensor}
                  selectedEvent={selectedEvent}
                  allSensors={allSensors}
                  onSensorSelect={handleSensorSelect}
                />
              </div>
            </div>

    
            <div className="lg:hidden h-full flex flex-col space-y-6 px-2">
 
              <div className="bg-white rounded-lg shadow-sm border flex-1 flex flex-col overflow-hidden min-h-96">
                <div className="p-4 border-b bg-gray-50 flex-shrink-0">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                    BK & Queens Sensors
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      ({filteredSensors.length})
                    </span>
                    <Cloud className="h-4 w-4 ml-2 text-gray-400" />
                  </h2>
                </div>
                <div className="flex-1 map-wrapper">
                  <FloodMap
                    sensors={filteredSensors}
                    selectedSensor={selectedSensor}
                    onSensorSelect={handleSensorSelect}
                  />
                </div>
              </div>

    
              {selectedSensor && (
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden max-h-96">
                  <TrendPanel selectedSensor={selectedSensor} />
                </div>
              )}

     
              {selectedEvent && (
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden max-h-96">
                  <EventDetails
                    selectedSensor={selectedSensor}
                    selectedEvent={selectedEvent}
                    allSensors={allSensors}
                    onSensorSelect={handleSensorSelect}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;